import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Layers,
  Scan,
  Package,
  MapPin,
  CheckCircle,
  AlertCircle,
  Loader,
  RotateCcw,
  History,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { ScanTextInput } from '../../../common/inputs';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { itemsService } from '../../../../services/itemsService';
import { stcBinService } from '../../../../services/stcBinService';
import { itemBinSizeService } from '../../../../services/itemBinSizeService';
import { dataInspectionService } from '../../../../services/dataInspectionService';
import { parseGS1Barcode } from '../../../../utils/gs1Parser';
import { CapacityUpdateModal } from '../../../shared/CapacityUpdateModal';
import { ItemsDto } from '../../../../types/items';
import { StcBinDto } from '../../../../types/stcBin';
import { ItemBinSizeDto } from '../../../../types/itemBinSize';
import { ItemBinsDto } from '../../../../types/dataInspection';

// ── Types ──────────────────────────────────────────────────────

interface CapacityRegistrationAppProps {
  onBack: () => void;
}

type ScanStep = 'item' | 'location';

interface HistoryEntry {
  itemNo: string;
  itemDescription: string;
  binCode: string;
  oldMaxQuantity: number | null;
  newMaxQuantity: number;
  timestamp: Date;
}

// ── Component ──────────────────────────────────────────────────

export const CapacityRegistrationApp: React.FC<CapacityRegistrationAppProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentWarehouse } = useWarehouse();
  const companyNo = user?.companyNo || '';

  // ── Step state ──
  const [step, setStep] = useState<ScanStep>('item');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Item state ──
  const [itemInput, setItemInput] = useState('');
  const [selectedItem, setSelectedItem] = useState<ItemsDto | null>(null);

  // ── Location state ──
  const [locationInput, setLocationInput] = useState('');
  const [selectedBin, setSelectedBin] = useState<StcBinDto | null>(null);

  // ── ItemBinSize + saldo state ──
  const [existingConfig, setExistingConfig] = useState<ItemBinSizeDto | null>(null);
  const [currentQuantity, setCurrentQuantity] = useState<number>(0);

  // ── Modal state ──
  const [showModal, setShowModal] = useState(false);

  // ── History ──
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // ── Refs ──
  const itemInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  // ── Scanner ──
  const isScannerEnabled = useBarcodeScannerEnabled();
  useSimpleBarcodeScanner({
    onScan: (barcode: string) => {
      const gs1 = parseGS1Barcode(barcode);
      const code = gs1?.gtin || barcode;

      if (step === 'item') {
        handleItemLookup(code);
      } else if (step === 'location') {
        handleLocationLookup(code);
      }
    },
    enabled: isScannerEnabled,
  });

  // ── Auto-focus on step change ──
  useEffect(() => {
    if (step === 'item') {
      setTimeout(() => itemInputRef.current?.focus(), 100);
    } else if (step === 'location') {
      setTimeout(() => locationInputRef.current?.focus(), 100);
    }
  }, [step]);

  // ── Item lookup ──
  const handleItemLookup = useCallback(async (input: string) => {
    if (!input.trim() || !companyNo) return;
    setLoading(true);
    setError(null);

    try {
      let item: ItemsDto | null = null;

      // Try exact itemNo first
      try {
        item = await itemsService.getItemByCompanyAndItemNo(companyNo, input.trim());
      } catch {
        // Try barcode
        try {
          item = await itemsService.getByBarcode(companyNo, input.trim());
        } catch {
          item = null;
        }
      }

      // TW 36320099: fallback — samlet oppslag som også matcher alternative strekkoder
      // (ItemUnits) og GTIN-13/14-varianter.
      if (!item) {
        try {
          const resolved = await itemsService.resolveBarcode(input.trim());
          if (resolved.length > 0) {
            item = resolved[0];
          }
        } catch {
          // Behold item = null — feilmeldingen under dekker
        }
      }

      if (!item) {
        setError(t('warehouse.modules.capacityRegistration.errors.itemNotFound', { '0': input.trim() }, `Fant ingen vare med nummer eller strekkode "${input.trim()}"`));
        return;
      }

      setSelectedItem(item);
      setItemInput(item.itemNo);
      setStep('location');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [companyNo, t]);

  // ── Location lookup ──
  const handleLocationLookup = useCallback(async (input: string) => {
    if (!input.trim() || !companyNo || !selectedItem) return;
    setLoading(true);
    setError(null);

    try {
      const bin = await stcBinService.getByExactBinCode(companyNo, input.trim());
      if (!bin) {
        setError(t('warehouse.modules.capacityRegistration.errors.locationNotFound', { '0': input.trim() }, `Fant ingen lokasjon "${input.trim()}"`));
        return;
      }

      setSelectedBin(bin);
      setLocationInput(bin.binCode);

      // Load existing ItemBinSize config and current quantity in parallel
      const [config, itemBins] = await Promise.all([
        itemBinSizeService.getByCompanyItemAndBinSize(companyNo, selectedItem.itemNo, bin.binSizeNo),
        dataInspectionService.getItemBinsByBinCode(bin.binCode) as Promise<ItemBinsDto[]>,
      ]);

      setExistingConfig(config);

      // Find this item's quantity at this location
      const itemBin = itemBins.find(ib => ib.itemNo === selectedItem.itemNo);
      setCurrentQuantity(itemBin?.quantity ?? 0);

      // Open modal automatically
      setShowModal(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [companyNo, selectedItem, t]);

  // ── Reset flow ──
  const resetToItem = () => {
    setStep('item');
    setItemInput('');
    setSelectedItem(null);
    setLocationInput('');
    setSelectedBin(null);
    setExistingConfig(null);
    setCurrentQuantity(0);
    setShowModal(false);
    setError(null);
  };

  const resetToLocation = () => {
    setStep('location');
    setLocationInput('');
    setSelectedBin(null);
    setExistingConfig(null);
    setCurrentQuantity(0);
    setShowModal(false);
    setError(null);
  };

  // ── Handle save from modal ──
  const handleCapacitySaved = (newMaxQuantity: number) => {
    if (selectedItem && selectedBin) {
      setHistory(prev => [{
        itemNo: selectedItem.itemNo,
        itemDescription: selectedItem.description1 || selectedItem.itemNo,
        binCode: selectedBin.binCode,
        oldMaxQuantity: existingConfig?.maxQuantity ?? null,
        newMaxQuantity,
        timestamp: new Date(),
      }, ...prev.slice(0, 19)]);
    }
    setShowModal(false);
    resetToItem();
  };

  // ── Render step indicator ──
  const renderStepIndicator = () => (
    <div className="flex items-center gap-2 mb-4">
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
        step === 'item'
          ? 'bg-[#0E7C86] text-white'
          : selectedItem ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
      }`}>
        <Package className="w-3.5 h-3.5" />
        {t('warehouse.modules.capacityRegistration.steps.item', {}, '1. Vare')}
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
        step === 'location'
          ? 'bg-[#0E7C86] text-white'
          : selectedBin ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
      }`}>
        <MapPin className="w-3.5 h-3.5" />
        {t('warehouse.modules.capacityRegistration.steps.location', {}, '2. Lokasjon')}
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
        showModal
          ? 'bg-[#0E7C86] text-white'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
      }`}>
        <Layers className="w-3.5 h-3.5" />
        {t('warehouse.modules.capacityRegistration.steps.capacity', {}, '3. Kapasitet')}
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack} />
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('warehouse.modules.capacityRegistration.title', {}, 'Kapasitetsregistrering')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('warehouse.modules.capacityRegistration.description', {}, 'Oppdater lokasjonskapasitet for varer')}</p>
          </div>
        </div>
        <button
          onClick={resetToItem}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          title={t('warehouse.modules.capacityRegistration.actions.restart', {}, 'Start på nytt')}
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {/* Step indicator */}
      {renderStepIndicator()}

      {/* Error */}
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-xs text-red-500 hover:text-red-700 mt-1"
            >
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 1: Scan Item ── */}
      {step === 'item' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0E7C86]/10 dark:bg-[#0E7C86]/20 flex items-center justify-center">
              <Scan className="w-5 h-5 text-[#0E7C86] dark:text-[#4fd1c5]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('warehouse.modules.capacityRegistration.itemStep.heading', {}, 'Skann eller skriv varenummer')}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('warehouse.modules.capacityRegistration.itemStep.subtitle', {}, 'Varenummer eller strekkode')}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <ScanTextInput
              ref={itemInputRef}
              autoFocus
              value={itemInput}
              onChange={(e) => setItemInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleItemLookup(itemInput); }}
              placeholder={t('warehouse.modules.capacityRegistration.itemStep.placeholder', {}, 'Varenummer / strekkode')}
              className="flex-1 px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0E7C86] focus:border-[#0E7C86] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              disabled={loading}
            />
            <button
              onClick={() => handleItemLookup(itemInput)}
              disabled={loading || !itemInput.trim()}
              className="px-5 py-3 bg-[#0E7C86] text-white rounded-lg hover:bg-[#0A5F66] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : t('common.buttons.search', {}, 'Søk')}
            </button>
          </div>

          {isScannerEnabled && <MobileBarcodeCaptureInput onScan={handleItemLookup} />}
        </div>
      )}

      {/* ── Step 2: Scan Location ── */}
      {step === 'location' && selectedItem && (
        <div className="space-y-4">
          {/* Selected item summary */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-3 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800 dark:text-green-300 truncate">
                {selectedItem.itemNo}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 truncate">
                {selectedItem.description1}
              </p>
            </div>
            <button
              onClick={resetToItem}
              className="text-xs text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium"
            >
              {t('warehouse.modules.capacityRegistration.actions.change', {}, 'Endre')}
            </button>
          </div>

          {/* Location input */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0E7C86]/10 dark:bg-[#0E7C86]/20 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#0E7C86] dark:text-[#4fd1c5]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('warehouse.modules.capacityRegistration.locationStep.heading', {}, 'Skann eller skriv lokasjonskode')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('warehouse.modules.capacityRegistration.locationStep.subtitle', {}, 'Lokasjonen du vil oppdatere kapasitet for')}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <ScanTextInput
                ref={locationInputRef}
                autoFocus
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLocationLookup(locationInput); }}
                placeholder={t('warehouse.modules.capacityRegistration.locationStep.placeholder', {}, 'Lokasjonskode')}
                className="flex-1 px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0E7C86] focus:border-[#0E7C86] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                disabled={loading}
              />
              <button
                onClick={() => handleLocationLookup(locationInput)}
                disabled={loading || !locationInput.trim()}
                className="px-5 py-3 bg-[#0E7C86] text-white rounded-lg hover:bg-[#0A5F66] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? <Loader className="w-5 h-5 animate-spin" /> : t('common.buttons.search', {}, 'Søk')}
              </button>
            </div>

            {isScannerEnabled && <MobileBarcodeCaptureInput onScan={handleLocationLookup} />}
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 animate-spin text-[#0E7C86]" />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{t('common.status.loading', {}, 'Laster...')}</span>
        </div>
      )}

      {/* ── Session History ── */}
      {history.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 w-full text-left text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 mb-2"
          >
            <History className="w-4 h-4" />
            {t('warehouse.modules.capacityRegistration.history.title', { '0': history.length }, `Siste oppdateringer (${history.length})`)}
            {showHistory ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
          </button>

          {showHistory && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
              {history.map((entry, i) => (
                <div key={i} className="px-4 py-3 flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {entry.itemNo} — {entry.binCode}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {entry.itemDescription}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-medium text-[#0E7C86] dark:text-[#4fd1c5]">
                      {entry.oldMaxQuantity != null ? `${entry.oldMaxQuantity} → ` : ''}{t('warehouse.modules.capacityRegistration.history.units', { '0': entry.newMaxQuantity }, `${entry.newMaxQuantity} stk`)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {entry.timestamp.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Capacity Update Modal ── */}
      {showModal && selectedItem && selectedBin && (
        <CapacityUpdateModal
          isOpen={true}
          onClose={() => {
            setShowModal(false);
            resetToLocation();
          }}
          onSaved={handleCapacitySaved}
          itemNo={selectedItem.itemNo}
          itemDescription={selectedItem.description1}
          binCode={selectedBin.binCode}
          binSizeNo={selectedBin.binSizeNo}
          currentQuantity={currentQuantity}
          currentMaxQuantity={existingConfig?.maxQuantity}
          existingItemBinSizeId={existingConfig?.id ?? null}
          existingPutAwayPriorityNo={existingConfig?.putAwayPriorityNo ?? selectedBin.putAwayPriorityNo ?? 1}
        />
      )}
    </div>
  );
};
